import { useMobile } from '@api/services/hooks/useMobile';
import { getAccountStore, PurchaseExtendedLogs } from '@api/store/account/actions';
import { AccountState } from '@api/store/account/types';
import { ShowDialog } from '@api/store/dialog/actions';
import { Link } from '@material-ui/core';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { Banner, Theme } from '@dev/ee-components';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useActivateBillingDialog } from '../../../../services/hooks/useActivateBillingDialog';
import { PurchaseExtendedLogsDialog } from '../PurchaseExtendedLogsDialog';

const ExtendedLogsAlert = styled.div`
    & .MuiAlert-root {
        margin: 32px 0;
        padding: 16px 48px;
        ${Theme.breakpoints.down('xs')} {
            padding: 16px;
        }
    }
`;

export const ExtendedLogsBanner: FC = () => {
    const { GeneralInfo } = useSelector(getAccountStore);
    const { t } = useTranslation();
    const isMobile = useMobile();
    const canSeeExtendedLogs = GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ExtendedLogs);

    const canSeeExtendedLogsText = canSeeExtendedLogs ? t('extlogs') : t('logs');

    const alertInfo = {
        title: canSeeExtendedLogsText,
        body: canSeeExtendedLogs ? (
            <Trans i18nKey="extendedlogspro_prompt">Currently we store your logs for up to 35 days.</Trans>
        ) : (
            <UpgradeLogsBannerBody />
        ),
    };

    return (
        <ExtendedLogsAlert>
            <Banner isMobile={isMobile} {...alertInfo} />
        </ExtendedLogsAlert>
    );
};

const UpgradeLogsBannerBody: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const { GeneralInfo, AdvancedOptions }: AccountState = useSelector(getAccountStore);
    const dispatch = useDispatch();
    const { showActivateBilling, isBillingActive } = useActivateBillingDialog();
    const storedLogsDays = GeneralInfo?.KeepLogsForDays;

    const upgradeLinkEnabled =
        !GeneralInfo?.IsSub && !AdvancedOptions?.IsOwnedByReseller && !GeneralInfo?.IsUser && GeneralInfo?.CanExtendLogs;

    const purchaseExtendedLogs = (): void => {
        dispatch(PurchaseExtendedLogs());
    };

    const activateExtendedLogsDialog = (): void => {
        if (!isBillingActive) {
            return showActivateBilling();
        }

        dispatch(
            ShowDialog({
                title: t('purchaseextendedlogs'),
                confirmLabel: t('purchase'),
                cancelLabel: t('cancel'),
                onClickConfirm: purchaseExtendedLogs,
                body: <PurchaseExtendedLogsDialog />,
            })
        );
    };
    return (
        <>
            {`${t('extendedlogs_prompt1', { days: storedLogsDays })} `}
            {upgradeLinkEnabled && (
                <Trans
                    i18nKey="extendedlogs_prompt2"
                    components={{
                        l: <Link onClick={activateExtendedLogsDialog} />,
                    }}
                />
            )}
        </>
    );
};
